/**
 * PDF Utilities
 * Functions for PDF manipulation and signature placement
 */

import { PDF_TEMPLATE } from "./constants";

/**
 * Calculate signature rectangle position for a PDF page
 * Scales the template coordinates to match the actual page dimensions
 * 
 * @param {Object} page - pdf-lib page object
 * @returns {Object} { x, y, rectW, rectH } - Rectangle coordinates and dimensions
 */
export function getSignatureRectForPage(page) {
  const pageWidth = page.getWidth();
  const pageHeight = page.getHeight();
  
  // Calculate scale factors
  const scaleX = pageWidth / PDF_TEMPLATE.BASE_WIDTH;
  const scaleY = pageHeight / PDF_TEMPLATE.BASE_HEIGHT;
  
  // Scale rectangle dimensions
  const rectW = PDF_TEMPLATE.SIGNATURE_RECT.WIDTH * scaleX;
  const rectH = PDF_TEMPLATE.SIGNATURE_RECT.HEIGHT * scaleY;
  
  // Calculate position (SHIFT_X/Y are NOT scaled)
  const x = PDF_TEMPLATE.SIGNATURE_RECT.X * scaleX + PDF_TEMPLATE.SIGNATURE_RECT.SHIFT_X;
  const y = pageHeight - ((PDF_TEMPLATE.SIGNATURE_RECT.Y_TOP * scaleY) + rectH) + PDF_TEMPLATE.SIGNATURE_RECT.SHIFT_Y;
  
  return { x, y, rectW, rectH };
}

/**
 * Calculate signature image dimensions to fit within rectangle
 * Maintains aspect ratio
 * 
 * @param {Object} image - pdf-lib image object
 * @param {number} maxWidth - Maximum width
 * @param {number} maxHeight - Maximum height
 * @returns {Object} { width, height } - Calculated dimensions
 */
export function calculateImageDimensions(image, maxWidth, maxHeight) {
  const aspectRatio = image.height / image.width;
  
  let width = maxWidth;
  let height = width * aspectRatio;
  
  // If height exceeds max, scale down
  if (height > maxHeight) {
    height = maxHeight;
    width = height / aspectRatio;
  }
  
  return { width, height };
}

/**
 * Center image within a rectangle
 * 
 * @param {Object} rect - Rectangle { x, y, width, height }
 * @param {Object} image - Image dimensions { width, height }
 * @returns {Object} { x, y } - Centered position
 */
export function centerImageInRect(rect, image) {
  return {
    x: rect.x + (rect.width - image.width) / 2,
    y: rect.y + (rect.height - image.height) / 2,
  };
}

/**
 * Get suggested filename for QR PDF
 * @param {string} storagePath - Original storage path
 * @returns {string} Suggested filename
 */
export function getQrPdfFilename(storagePath) {
  if (!storagePath) return "documento_QR.pdf";
  
  const basename = storagePath.split("/").pop() || "documento";
  
  // Remove .pdf extension if present
  const nameWithoutExt = basename.replace(/\.pdf$/i, "");
  
  return `${nameWithoutExt}_QR.pdf`;
}

/**
 * Convert data URL to Blob
 * @param {string} dataUrl - Data URL
 * @returns {Blob} Blob object
 */
export function dataUrlToBlob(dataUrl) {
  const parts = dataUrl.split(",");
  const mime = parts[0].match(/:(.*?);/)[1];
  const bstr = atob(parts[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  
  return new Blob([u8arr], { type: mime });
}
